// Schedule › Team manager › Who's working, Blueprint only (pages/ux-full schedule/team), on ListPage
// (BL-189): one of Team manager's tabs with Time off and Hours worked (TeamTabs.tsx). The figures lead
// the title block; The team is the list (RowList), and the day of the one picked (else the first
// running late, else the first) is beside it (TechStops). Reads dispatch.board and staff.list (for what
// each does), and re-reads on the board's live changes.
import { useMemo, useState } from 'react';
import { dateText } from '@flowology/kernel';
import { Button, Figures, Link, RowList, TechStops, teamRows } from '@flowology/ui-web/fl';
import { ListPage, type ListStatus } from '@flowology/ui-web/patterns';
import type { Board } from '../board-model.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { todayIn } from '../books/model.ts';
import { useQuery } from '../books/ui.tsx';
import { useLive, useNow } from './live.ts';
import { pensBy, teamFigures, teamLede, teamRow, techDayProps } from './pages-model.ts';
import { TeamTabs } from './TeamTabs.tsx';
import './team-manager.css';

/** How many of the team show before Show all. */
const FIRST = 5;

export function TeamScreen({
  nav,
  timeZone,
  onBoard,
}: {
  nav: NavProps;
  timeZone: string;
  /** The day board. */
  onBoard: () => void;
}) {
  const today = todayIn(timeZone);
  const now = useNow();
  const board = useQuery<Board>('dispatch.board', { date: today });
  const staff = useQuery<{ staff: { id: string; skills: string[] }[] }>('staff.list', {
    techniciansOnly: true,
  });
  useLive(() => void board.reload());
  const [pick, setPick] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const crumb = usePageCrumb(nav);
  const b = board.data && board.data.date === today ? board.data : null;
  const pens = useMemo(() => (b ? pensBy(b.technicians) : new Map<string, number>()), [b]);
  const rows = b
    ? b.technicians.map((t) =>
        teamRow(
          t,
          pens.get(t.id) ?? null,
          staff.data?.staff.find((s) => s.id === t.id)?.skills ?? [],
          b,
          today,
          now,
        ),
      )
    : [];
  const fig = teamFigures(rows);
  // the one shown: picked, else the first running late, else the first
  const shownId = pick ?? rows.find((r) => r.late)?.id ?? rows[0]?.id ?? null;
  const tech = b?.technicians.find((t) => t.id === shownId);
  const status: ListStatus = b ? (rows.length ? 'ready' : 'empty') : board.error ? 'failed' : 'loading';
  return (
    <div className="app">
      <TeamTabs nav={nav} />
      <main className="bp-team-main">
        <PageBanners nav={nav} />
        <ListPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'Who’s working today',
            lede: rows.length
              ? teamLede(rows, fig)
              : 'What each technician is doing now, and how full their day is.',
            actions: (
              <Button variant="primary" onPress={onBoard}>
                The board
              </Button>
            ),
            ...(rows.length
              ? {
                  figures: (
                    <Figures
                      items={[
                        { label: 'On a job', value: fig.working },
                        { label: 'On the way', value: fig.driving },
                        { label: 'Free', value: fig.free },
                        {
                          label: 'Running late',
                          value: <span className={fig.late ? 'bp-team-late' : undefined}>{fig.late}</span>,
                        },
                        { label: 'Off today', value: fig.off },
                      ]}
                    />
                  ),
                }
              : {}),
          }}
          list={{
            title: 'The team',
            ...(rows.length ? { count: rows.length } : {}),
            status,
            what: 'who’s working',
            emptyText: 'No technicians yet. The owner adds them under Setup › Company.',
            failedText: board.error ?? 'Who’s working didn’t load.',
            onRetry: () => void board.reload(),
            children: (
              <>
                <RowList
                  rows={teamRows(
                    { techs: all ? rows : rows.slice(0, FIRST) },
                    { selected: shownId, onPick: setPick },
                  )}
                />
                {rows.length > FIRST && !all && (
                  <p className="bp-team-more">
                    <Link onPress={() => setAll(true)}>Show all {rows.length}</Link>
                  </p>
                )}
              </>
            ),
          }}
          {...(b && tech
            ? {
                aside: {
                  title: `${tech.name}’s day`,
                  children: (
                    <TechStops
                      data={techDayProps(
                        tech,
                        pens.get(tech.id) ?? null,
                        b.timeZone,
                        today,
                        `Today, ${dateText(today, 'UTC', { year: false, weekday: 'long' })}`,
                        now,
                      )}
                    />
                  ),
                },
              }
            : {})}
        />
      </main>
    </div>
  );
}
